import { DbSupportedCountryCode } from '@lessonup/domain-utils';
import { z } from 'zod';

type SupportedSchoolTypeCountry = Extract<DbSupportedCountryCode, 'be' | 'fr' | 'gb' | 'nl' | 'us'>;

export const lessonSchoolTypesByCountry = {
  be: ['ko', 'lo', 'blo', 'so', 'bso', 'ho', 'dko'],
  fr: ['pe', 'se', 'te', 've', 'unv', 'sp'],
  gb: ['gb_pe', 'gb_lse', 'gb_use', 'gb_fe', 'gb_hed', 'gb_hen', 'gb_fd', 'gb_sp'],
  nl: ['po', 'vo', 'pr', 'nl_so', 'vso', 'mbo', 'hbo', 'wo', 'bo', 'nl_isk'],
  us: ['us_kin', 'us_es', 'us_ms', 'us_hs', 'us_col'],
} as const satisfies Record<SupportedSchoolTypeCountry, readonly string[]>;

export const internationalLessonSchoolTypes = ['pe', 'se', 'te', 've', 'unv', 'sp'] as const;

export const DbLessonInternationalSchoolTypeSchema = z.enum(internationalLessonSchoolTypes);
export const DbLessonBelgiumSchoolTypeSchema = z.enum(lessonSchoolTypesByCountry.be);
export const DbLessonFranceSchoolTypeSchema = z.enum(lessonSchoolTypesByCountry.fr);
export const DbLessonGreatBritainSchoolTypeSchema = z.enum(lessonSchoolTypesByCountry.gb);
export const DbLessonNetherlandsSchoolTypeSchema = z.enum(lessonSchoolTypesByCountry.nl);
export const DbLessonUnitedStatesSchoolTypeSchema = z.enum(lessonSchoolTypesByCountry.us);

export const DbLessonSchoolTypeSchema = z.enum(
  // Remove duplicates by creating a Set and converting it back to an Array
  Array.from(
    new Set([
      ...DbLessonBelgiumSchoolTypeSchema.options,
      ...DbLessonFranceSchoolTypeSchema.options,
      ...DbLessonGreatBritainSchoolTypeSchema.options,
      ...DbLessonInternationalSchoolTypeSchema.options,
      ...DbLessonNetherlandsSchoolTypeSchema.options,
      ...DbLessonUnitedStatesSchoolTypeSchema.options,
    ])
  )
);

export type DbLessonInternationalSchoolType = z.infer<typeof DbLessonInternationalSchoolTypeSchema>;
export type DbLessonBelgiumSchoolType = z.infer<typeof DbLessonBelgiumSchoolTypeSchema>;
export type DbLessonFranceSchoolType = z.infer<typeof DbLessonFranceSchoolTypeSchema>;
export type DbLessonGreatBritainSchoolType = z.infer<typeof DbLessonGreatBritainSchoolTypeSchema>;
export type DbLessonNetherlandsSchoolType = z.infer<typeof DbLessonNetherlandsSchoolTypeSchema>;
export type DbLessonUnitedStatesSchoolType = z.infer<typeof DbLessonUnitedStatesSchoolTypeSchema>;
export type DbLessonSchoolType = z.infer<typeof DbLessonSchoolTypeSchema>;

/**
 * Maps country codes to their corresponding school type arrays
 * This serves as a single source of truth for country-to-school-type mappings
 */
export type CountrySchoolTypeMap = {
  be: DbLessonBelgiumSchoolType[];
  fr: DbLessonFranceSchoolType[];
  gb: DbLessonGreatBritainSchoolType[];
  nl: DbLessonNetherlandsSchoolType[];
  us: DbLessonUnitedStatesSchoolType[];
};

/**
 * Gets the appropriate school type array based on the provided country code
 * For country codes not explicitly mapped, returns the International school type
 */
export type DbLessonCountrySchoolType<TCountryCode extends DbSupportedCountryCode> =
  TCountryCode extends keyof CountrySchoolTypeMap
    ? CountrySchoolTypeMap[TCountryCode]
    : DbLessonInternationalSchoolType[];

/**
 * Gets the school type array for a given country code.
 * @param countryCode - The country code to get school types for
 * @returns An array of school types specific to the country.
 * For unsupported country codes, returns international school types.
 */
export function getDbLessonSchoolTypeByCountryCode<TCountryCode extends DbSupportedCountryCode>(
  countryCode: DbSupportedCountryCode
): DbLessonCountrySchoolType<TCountryCode> {
  switch (countryCode) {
    case 'be':
      return DbLessonBelgiumSchoolTypeSchema.options as DbLessonCountrySchoolType<TCountryCode>;
    case 'fr':
      return DbLessonFranceSchoolTypeSchema.options as DbLessonCountrySchoolType<TCountryCode>;
    case 'gb':
      return DbLessonGreatBritainSchoolTypeSchema.options as DbLessonCountrySchoolType<TCountryCode>;
    case 'nl':
      return DbLessonNetherlandsSchoolTypeSchema.options as DbLessonCountrySchoolType<TCountryCode>;
    case 'us':
      return DbLessonUnitedStatesSchoolTypeSchema.options as DbLessonCountrySchoolType<TCountryCode>;
    default:
      return DbLessonInternationalSchoolTypeSchema.options as DbLessonCountrySchoolType<TCountryCode>;
  }
}

/**
 * Returns an array containing all available lesson school types across all countries
 */
export function getAllDbLessonSchoolTypes(): DbLessonSchoolType[] {
  return DbLessonSchoolTypeSchema.options;
}
